<script setup lang="ts">
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import { useModal } from '@/composition/use-modal';
  import {
    END_OF_DAY,
    IScheduleBlock,
    IScheduleDialogResult,
    IScheduleForm,
    newScheduleBlock,
    SCHEDULE_DAY_PRESETS,
    SCHEDULE_TIME_PRESETS,
    SCHEDULE_WEEK,
    scheduleFormFromRanges,
    scheduleRangesFromForm,
    TScheduleDayPreset,
    TScheduleError,
    TScheduleTimePreset,
    validateScheduleForm,
  } from '@/domain/catalog/schedule-form';
  import { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';

  /**
   * «Horarios» (plan §5.14): «Todo el día | Solo en ciertos horarios», time
   * blocks with day chips, shortcuts. «Listo» hands the rows to the editor
   * (its «Guardar» commits them); closing resolves null (cancelled).
   */
  const props = defineProps<{
    /** The vue-js-modal name it was opened with. */
    dialogName: string;
    /** The product's or category's name. */
    name: string;
    hourRanges?: ISimpleHourRange[] | null;
    entity: 'item' | 'category';
  }>();

  const modals = useModal();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const form = ref<IScheduleForm>(
    scheduleFormFromRanges(props.hourRanges ?? null)
  );
  const submitted = ref(false);

  const errors = computed(() => validateScheduleForm(form.value));
  const valid = computed(() => Object.keys(errors.value).length === 0);

  const modes = computed(() => [
    { id: 'always', label: t('menuPage.schedule.always') },
    { id: 'some', label: t('menuPage.schedule.some') },
  ]);

  const DAY_PRESETS: TScheduleDayPreset[] = ['everyDay', 'weekdays', 'weekend'];
  const TIME_PRESETS: TScheduleTimePreset[] = ['lunch', 'night'];

  const ERROR_KEYS: Record<TScheduleError, string> = {
    noBlocks: 'menuPage.schedule.errors.noBlocks',
    noDays: 'menuPage.schedule.errors.noDays',
    order: 'menuPage.schedule.errors.order',
  };

  function errorOf(key: string): string | null {
    const error = errors.value[key];
    return error && (submitted.value || error === 'order')
      ? t(ERROR_KEYS[error])
      : null;
  }

  /** An end before the start: how to cover the hours past midnight. */
  function crossesMidnight(key: string): boolean {
    return errors.value[key] === 'order';
  }

  /** «12:00» → «12»; the end of the day reads «24». */
  function hourText(time: string): string {
    if (time === END_OF_DAY) return '24';
    const [hours, minutes] = time.split(':');
    return minutes === '00'
      ? String(Number(hours))
      : `${Number(hours)}:${minutes}`;
  }

  /** «Almuerzo (12 a 16)». */
  function timePresetLabel(preset: TScheduleTimePreset): string {
    const { from, to } = SCHEDULE_TIME_PRESETS[preset];
    return t('menuPage.schedule.presetHours', {
      name: t(`menuPage.schedule.preset.${preset}`),
      from: hourText(from),
      to: hourText(to),
    });
  }

  function setMode(mode: string) {
    const next = mode === 'some' ? 'some' : 'always';
    form.value = {
      mode: next,
      blocks:
        next === 'some' && !form.value.blocks.length
          ? [newScheduleBlock()]
          : form.value.blocks,
    };
  }

  function updateBlock(key: string, patch: Partial<IScheduleBlock>) {
    form.value = {
      ...form.value,
      blocks: form.value.blocks.map((block) =>
        block.key === key ? { ...block, ...patch } : block
      ),
    };
  }

  function toggleDay(block: IScheduleBlock, day: number) {
    updateBlock(block.key, {
      days: block.days.includes(day)
        ? block.days.filter((candidate) => candidate !== day)
        : SCHEDULE_WEEK.filter(
            (candidate) => candidate === day || block.days.includes(candidate)
          ),
    });
  }

  function addBlock() {
    form.value = {
      ...form.value,
      blocks: [...form.value.blocks, newScheduleBlock()],
    };
  }

  function removeBlock(key: string) {
    form.value = {
      ...form.value,
      blocks: form.value.blocks.filter((block) => block.key !== key),
    };
  }

  /** Shortcuts shape the last block (a first one when there is none). */
  function lastBlock(): IScheduleBlock {
    const blocks = form.value.blocks;
    if (blocks.length) return blocks[blocks.length - 1];
    const block = newScheduleBlock();
    form.value = { ...form.value, blocks: [block] };
    return block;
  }

  function applyDays(preset: TScheduleDayPreset) {
    updateBlock(lastBlock().key, { days: [...SCHEDULE_DAY_PRESETS[preset]] });
  }

  function applyTime(preset: TScheduleTimePreset) {
    updateBlock(lastBlock().key, { ...SCHEDULE_TIME_PRESETS[preset] });
  }

  function answer(result: IScheduleDialogResult | null) {
    // Closes and unmounts the dialog: nothing may run after this.
    modals.dialogSubmit(props.dialogName, result);
  }

  function done() {
    submitted.value = true;
    if (!valid.value) return;
    answer({
      hourRanges: scheduleRangesFromForm(form.value, props.hourRanges ?? null),
    });
  }
  // Focus inside on open: Esc closes it and Tab starts here.
  const root = ref<HTMLElement | null>(null);
  onMounted(() => root.value?.focus({ preventScroll: true }));
</script>

<template>
  <div
    ref="root"
    class="v2-catalog-schedule-dialog"
    role="dialog"
    tabindex="-1"
    :aria-label="t('menuPage.schedule.title', { name })"
    @keydown.esc.stop="answer(null)"
  >
    <h2 class="schedule-title">{{ t('menuPage.schedule.title', { name }) }}</h2>

    <PosUiSegmented
      class="schedule-mode"
      :value="form.mode"
      :items="modes"
      block
      @input="setMode"
    />

    <div v-if="form.mode === 'some'" class="schedule-body">
      <div
        v-for="(block, index) in form.blocks"
        :key="block.key"
        class="schedule-block"
      >
        <div
          class="block-days"
          role="group"
          :aria-label="t('menuPage.schedule.days')"
        >
          <button
            v-for="day in SCHEDULE_WEEK"
            :key="day"
            type="button"
            class="day-chip"
            :class="{ 'is-on': block.days.includes(day) }"
            :aria-pressed="block.days.includes(day) ? 'true' : 'false'"
            :aria-label="t(`menuPage.weekday.${day}`)"
            @click="toggleDay(block, day)"
          >
            {{ t(`menuPage.schedule.dayLetter.${day}`) }}
          </button>
        </div>
        <div class="block-times">
          <label class="time-field">
            <span class="time-label">{{ t('menuPage.schedule.from') }}</span>
            <input
              type="time"
              step="900"
              class="time-input"
              :value="block.from"
              @change="updateBlock(block.key, { from: $event.target.value })"
            />
          </label>
          <label class="time-field">
            <span class="time-label">{{ t('menuPage.schedule.to') }}</span>
            <input
              type="time"
              step="900"
              class="time-input"
              :value="block.to"
              @change="updateBlock(block.key, { to: $event.target.value })"
            />
          </label>
          <button
            type="button"
            class="block-remove"
            :aria-label="t('menuPage.schedule.remove', { n: index + 1 })"
            :title="t('menuPage.schedule.remove', { n: index + 1 })"
            @click="removeBlock(block.key)"
          >
            <i class="mdi mdi-delete-outline" />
          </button>
        </div>
        <p v-if="errorOf(block.key)" class="block-error">
          {{ errorOf(block.key) }}
        </p>
        <p v-if="crossesMidnight(block.key)" class="block-hint">
          {{ t('menuPage.schedule.midnightHint') }}
        </p>
      </div>

      <p v-if="errorOf('form')" class="block-error">{{ errorOf('form') }}</p>

      <PosUiButton
        class="schedule-add"
        color="neutral"
        variant="ghost"
        icon="plus"
        @click="addBlock"
        >{{ t('menuPage.schedule.add') }}</PosUiButton
      >

      <div class="schedule-shortcuts">
        <span class="shortcuts-label">{{
          t('menuPage.schedule.shortcuts')
        }}</span>
        <button
          v-for="preset in DAY_PRESETS"
          :key="preset"
          type="button"
          class="shortcut"
          @click="applyDays(preset)"
        >
          {{ t(`menuPage.schedule.preset.${preset}`) }}
        </button>
        <button
          v-for="preset in TIME_PRESETS"
          :key="preset"
          type="button"
          class="shortcut"
          @click="applyTime(preset)"
        >
          {{ timePresetLabel(preset) }}
        </button>
      </div>
    </div>

    <p v-if="entity === 'category'" class="schedule-note">
      {{ t('menuPage.schedule.categoryNote') }}
      {{ t('menuPage.schedule.categoryPosNote') }}
    </p>

    <div class="schedule-actions">
      <PosUiButton color="neutral" variant="ghost" @click="answer(null)">{{
        $t('cancel')
      }}</PosUiButton>
      <PosUiButton class="schedule-done" @click="done">{{
        t('menuPage.select.done')
      }}</PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-schedule-dialog {
    @apply tw-flex tw-flex-col tw-bg-surface tw-px-5 tw-pb-4 tw-pt-5;

    // Focused on open (no ring: it is the dialog itself).
    &:focus {
      @apply tw-outline-none;
    }

    .schedule-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    .schedule-mode {
      @apply tw-mt-3;
    }

    .schedule-body {
      @apply tw-mt-3;
    }

    .schedule-block {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-p-3;

      & + .schedule-block {
        @apply tw-mt-2;
      }
    }

    // Margins, not flex gap (Chrome < 84).
    .block-days {
      @apply -tw-mx-0.5 tw-flex tw-flex-wrap;

      // A circle for letters; languages without short day names get pills.
      .day-chip {
        @apply tw-m-0.5 tw-flex tw-h-10 tw-min-w-10 tw-px-2 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-neutral-100 tw-text-sm tw-font-semibold tw-text-neutral-700;

        &.is-on {
          @apply tw-bg-neutral-900 tw-text-on-inverse;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .block-times {
      @apply tw-mt-2 tw-flex tw-flex-wrap tw-items-end;

      .time-field {
        @apply tw-mr-2 tw-flex tw-flex-col;
      }

      .time-label {
        @apply tw-mb-1 tw-text-xs tw-font-medium tw-text-neutral-500;
      }

      .time-input {
        @apply tw-h-11 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-sm tw-tabular-nums tw-text-neutral-900;

        &:focus {
          @apply tw-border-neutral-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }

      .block-remove {
        @apply tw-ml-auto tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;

        &:hover {
          @apply tw-bg-red-50 tw-text-red-600;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .block-error {
      @apply tw-m-0 tw-mt-2 tw-text-xs tw-leading-snug tw-text-red-600;
    }

    .block-hint {
      @apply tw-m-0 tw-mt-1 tw-text-xs tw-leading-snug tw-text-neutral-500;
    }

    .schedule-add {
      @apply tw-mt-2;
    }

    // Margins, not flex gap (Chrome < 84).
    .schedule-shortcuts {
      @apply -tw-mx-1 tw-mt-3 tw-flex tw-flex-wrap tw-items-center;

      .shortcuts-label {
        @apply tw-mx-1 tw-text-xs tw-font-medium tw-text-neutral-500;
      }

      .shortcut {
        @apply tw-m-1 tw-inline-flex tw-h-11 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-0 tw-bg-neutral-100 tw-px-3 tw-text-xs tw-font-medium tw-text-neutral-700;

        &:hover {
          @apply tw-bg-neutral-200;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .schedule-note {
      @apply tw-mb-0 tw-mt-3 tw-text-sm tw-text-neutral-500;
    }

    // Margins, not flex gap (Chrome < 84).
    .schedule-actions {
      @apply tw-mt-4 tw-flex tw-items-center tw-justify-end;

      .schedule-done {
        @apply tw-ml-2;
      }
    }
  }
</style>
